/* ================================================================
   sd-motion.js — Senpai Drip global motion engine
   Auto-tags the DOM. No section files need editing.
   Opt any element (and its children) out with data-sd-still.

   Perf model:
   - glow moves with transform only (was repainting the viewport)
   - DOM rescans are scoped to added nodes, not the whole document
   - tilt/magnetic tag only on fine pointers
   - off-screen sections get content-visibility
   - live fps probe demotes the tier if the device can't keep up

   Style-recalc model (Sept 2026 — this is the expensive part):
   - tag() runs in two phases. EVERY getComputedStyle read for the
     whole document happens before the first attribute is written.
     Previously reads and writes alternated per section: each
     setAttribute invalidated style, so the next getComputedStyle
     forced a fresh recalc. Up to 1,500 of those before first paint.
   - hasFixedInside() no longer walks every descendant. It matches a
     selector first (free — no style resolution) and only resolves
     style for the handful of elements that could plausibly be fixed.
   - sweepHidden() reads all rects, then writes all classes. It used
     to alternate, which is layout thrash on every scroll pause.
   - the end-of-load content-visibility flush runs in small chunks
     instead of un-skipping the whole page in one task.

   Oct 2026 — freeze pass:
   - boot reads for tag() AND liftBodyChildren() all happen before the
     first write, so startup pays one full style recalc instead of two
   - mutation rescans are batched: one read pass across every new node,
     then one write pass. Previously up to 40 tag() calls ran back to
     back, each read forcing a fresh recalc after the previous write.
     Nodes already inside another queued node are dropped first.
   - end-of-load warm-up un-skips one section per idle slice (was three)

   Oct 2026 — tap → product page zoom:
   - the black .sd-fade curtain is gone wherever the browser supports
     cross-page View Transitions (iOS 18.2+, Chrome 126+); other links
     navigate natively and sd-motion.css crossfades the pages
   - tapping a product card springs that photo, the INSTANT it's
     tapped, from the card to fill the whole screen (corners squaring
     off), then it pushes in slowly with a loading line while the page
     loads behind it. The product page crossfades in over it (or swaps
     in on older browsers). Works on every browser; off for reduced
     motion.
   - a sharper copy of the photo is fetched at LOW priority and swapped
     in, so it isn't soft at full-screen size and never slows the page
   - tap to cancel; it also clears itself after 15s and on Back/Forward
     restores
   - window.SDMorph = { can, zoom(img) } — the quick-add sheet and the
     garment switcher use the same zoom (name(img) kept as an alias)
   - the product gallery is never tagged for reveal, so it can't go
     invisible under the crossfade
   - fixed: the card-press block in tagWrite() was nested inside itself

   Reliability:
   - when a skipped (content-visibility) section is warmed, its hidden
     children are re-checked so the reveal observer can't miss them
   - the watchdog re-runs whenever scrolling pauses (not just once),
     and on Back/Forward restores, so nothing stays invisible
   - sections containing ANY position:fixed layer are never tagged for
     reveal: a transformed ancestor re-anchors fixed descendants to
     itself, which displaced the hero frame and the 3D logo until the
     reveal fired and cleared the transform
   ================================================================ */
(function () {
  'use strict';

  var root = document.documentElement;
  if (root.classList.contains('sd-motion')) return; // never double-init
  root.classList.add('sd-motion');

  var tier = root.getAttribute('data-sd-tier') || 'full';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(hover:hover) and (pointer:fine)').matches;

  /* cross-page View Transitions (pagereveal ships alongside them) */
  var canVT = !reduce && ('onpagereveal' in window);

  /* the product page gallery — never revealed, so it's there the
     instant the page crossfades in */
  var VT_TARGET = '.section-main-product .media-gallery-card';

  var rIC = window.requestIdleCallback || function (fn) {
    return setTimeout(function () { fn({ timeRemaining: function () { return 8; } }); }, 1);
  };

  var HIDDEN = '[data-sd-in]:not(.sd-vis), [data-sd-head]:not(.sd-vis), [data-sd-img]:not(.sd-vis)';

  /* ---------- helpers ---------- */
  function still(el) {
    return !!(el && el.closest && el.closest('[data-sd-still], ' + VT_TARGET));
  }
  function each(sel, ctx, fn) {
    ctx = ctx || document;
    if (ctx.nodeType === 1 && ctx.matches && ctx.matches(sel)) fn(ctx, 0);
    var list = ctx.querySelectorAll(sel);
    for (var i = 0; i < list.length; i++) fn(list[i], i);
  }

  /* Does this element, or anything inside it, use position:fixed?
     A transform on an ancestor of a fixed element makes that ancestor the
     containing block, which moves the fixed layer off the viewport — so
     any section holding one must never be tagged for reveal.

     querySelector matching costs nothing (no style resolution), so we
     narrow to elements that could plausibly be fixed, then resolve style
     only for those. Direct children are always checked too, since a
     fixed overlay is usually a direct child. */
  var FIXED_HINT = '[id^="sd-hd-"],[id^="sd-l3d-"],[id^="sd-hd-dr-"],' +
    '.sd-hero__frame,.sd-bg,.sd-fade,' +
    '[class*="fixed"],[class*="sticky"],[class*="dock"],[class*="drawer"],' +
    '[class*="float"],[class*="overlay"],[class*="modal"],[class*="popup"],' +
    '[class*="sheet"],[class*="toast"],[class*="banner"]';

  function isFixed(el) {
    return getComputedStyle(el).position === 'fixed';
  }

  function hasFixedInside(el) {
    if (isFixed(el)) return true;

    var kids = el.children, i;
    for (i = 0; i < kids.length; i++) {
      if (isFixed(kids[i])) return true;
    }

    var hints = el.querySelectorAll(FIXED_HINT);
    for (i = 0; i < hints.length && i < 60; i++) {
      if (isFixed(hints[i])) return true;
    }
    return false;
  }

  /* ---------- 1. background layers ---------- */
  var blobA = null, blobB = null;

  function buildBg() {
    if (document.querySelector('.sd-bg')) return;
    var bg = document.createElement('div');
    bg.className = 'sd-bg';
    bg.setAttribute('aria-hidden', 'true');
    bg.innerHTML =
      '<div class="sd-bg__glow">' +
        '<div class="sd-bg__blob sd-bg__blob--a"></div>' +
        '<div class="sd-bg__blob sd-bg__blob--b"></div>' +
      '</div>' +
      '<div class="sd-bg__grain"></div>' +
      '<div class="sd-bg__vignette"></div>';
    document.body.insertBefore(bg, document.body.firstChild);
    blobA = bg.querySelector('.sd-bg__blob--a');
    blobB = bg.querySelector('.sd-bg__blob--b');
  }

  /* ---------- 2. auto-tagging ---------- */
  var SECTIONS = '.shopify-section > *, [id^="shopify-section"] > *, main section';
  var HEADS = 'h1, h2, .sd-hero__title, [class*="section-title"], [class*="section-heading"]';
  var IMGS = '[class*="card"] [class*="media"], [class*="product-card"] [class*="image"], .media-wrapper';
  var CARDS = '[class*="product-card"], [class*="card-wrapper"], .sd-card, [class*="collection-card"]';
  var BTNS = '.button, button[type="submit"], [class*="btn"], .shopify-payment-button__button';

  /* PHASE 1: read only. Nothing here may write to the DOM, or the next
     read forces a fresh style recalc. Can be called for several contexts
     into one plan, so a batch of new nodes pays for ONE recalc. */
  function tagRead(ctx, plan) {
    ctx = ctx || document;
    plan = plan || { ctxs: [], sections: [], fixed: [], delay: [], heads: [], big: [], imgs: [] };
    plan.ctxs.push(ctx);

    var secs = [], i;
    each(SECTIONS, ctx, function (el) {
      if (still(el) || el.hasAttribute('data-sd-in')) return;
      // the product gallery must be visible the instant the page lands
      if (el.querySelector(VT_TARGET)) return;
      secs.push(el);
    });
    for (i = 0; i < secs.length; i++) {
      plan.sections.push(secs[i]);
      plan.fixed.push(hasFixedInside(secs[i]));
      plan.delay.push(Math.min(i, 5) * 70); // stagger counts per context, as before
    }

    each(HEADS, ctx, function (el) {
      if (still(el) || el.hasAttribute('data-sd-head')) return;
      plan.heads.push(el);
      plan.big.push((parseFloat(getComputedStyle(el).fontSize) || 0) >= 34);
    });

    each(IMGS, ctx, function (el) {
      if (still(el) || el.hasAttribute('data-sd-img')) return;
      if (!el.querySelector('img')) return;
      plan.imgs.push(el);
    });

    return plan;
  }

  /* PHASE 2: write only. */
  function tagWrite(plan) {
    var i;
    for (i = 0; i < plan.sections.length; i++) {
      if (plan.fixed[i]) {
        plan.sections[i].setAttribute('data-sd-hasfixed', '');
      } else {
        plan.sections[i].setAttribute('data-sd-in', '');
        plan.sections[i].style.setProperty('--sd-d', plan.delay[i] + 'ms');
      }
    }
    for (i = 0; i < plan.heads.length; i++) {
      plan.heads[i].setAttribute('data-sd-head', '');
      plan.heads[i].removeAttribute('data-sd-in');
      if (plan.big[i]) plan.heads[i].classList.add('sd-sheen');
    }
    for (i = 0; i < plan.imgs.length; i++) {
      plan.imgs[i].setAttribute('data-sd-img', '');
    }

    if (reduce) return;

    for (var c = 0; c < plan.ctxs.length; c++) {
      each(CARDS, plan.ctxs[c], function (el) {
        if (still(el) || el.hasAttribute('data-sd-press')) return;
        el.setAttribute('data-sd-press', '');          // tactile press, all devices
      });
      each(BTNS, plan.ctxs[c], function (el) {
        if (still(el) || el.hasAttribute('data-sd-press')) return;
        el.setAttribute('data-sd-press', '');
        if (fine) el.setAttribute('data-sd-mag', '');
      });
    }
  }

  /* single-context convenience (theme editor) */
  function tag(ctx) { tagWrite(tagRead(ctx)); }

  /* one-off boot pass (never re-run on mutation) — split so its reads
     share the same recalc as tag()'s reads */
  function liftRead() {
    var want = [];
    each('body > *:not(.sd-bg):not(.sd-fade)', document, function (el) {
      if (el.classList.contains('sd-lift')) return;
      if (getComputedStyle(el).position === 'static') want.push(el);
    });
    return want;
  }
  function liftWrite(want) {
    for (var i = 0; i < want.length; i++) want[i].classList.add('sd-lift');
  }

  /* ---------- 3. reveal observer ---------- */
  var io = null;
  function observe(ctx) {
    if (reduce) {
      each('[data-sd-in], [data-sd-head], [data-sd-img]', ctx, function (el) {
        el.classList.add('sd-vis');
      });
      return;
    }
    if (!io && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          entries[i].target.classList.add('sd-vis');
          io.unobserve(entries[i].target);
        }
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    }
    each('[data-sd-in], [data-sd-head], [data-sd-img]', ctx, function (el) {
      if (el.classList.contains('sd-vis')) return;
      if (io) { io.observe(el); } else { el.classList.add('sd-vis'); }
    });
  }

  /* Re-observe still-hidden elements inside a scope. unobserve+observe
     forces the observer to deliver a fresh first reading, which fixes
     elements it lost track of while their section was skipped. They
     still get their normal reveal animation. */
  function recheck(scope) {
    if (!io) return;
    each(HIDDEN, scope || document, function (el) {
      io.unobserve(el);
      io.observe(el);
    });
  }

  /* Sections start skipped, then PRE-WARM about 1.5 viewports before they
     are seen. The layout+paint still happens — it just happens off-screen
     instead of in the frame the user is looking at. Once a section is
     warmed it keeps normal rendering, so the hitch can only ever be paid
     once, out of view. */
  var warmIO = null;

  function warmSection(el) {
    if (!el.classList.contains('sd-cv')) return;
    requestAnimationFrame(function () {
      el.classList.remove('sd-cv');
      // contents now have real layout — give the reveal observer a fresh look
      requestAnimationFrame(function () { recheck(el); });
    });
  }

  function skipOffscreenSections() {
    var main = document.getElementById('MainContent');
    if (!main) return;
    var secs = main.querySelectorAll(':scope > .shopify-section, :scope > section');

    if ('IntersectionObserver' in window) {
      warmIO = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          warmIO.unobserve(entries[i].target);
          warmSection(entries[i].target);
        }
      }, { rootMargin: '150% 0px 150% 0px' });
    }

    // leave the first two alone — they're at or near the fold
    for (var i = 2; i < secs.length; i++) {
      var s = secs[i];
      if (s.querySelector('[data-sd-still]')) continue;
      s.classList.add('sd-cv');
      if (warmIO) { warmIO.observe(s); } else { warmSection(s); }
    }
  }

  /* watchdog: nothing may stay invisible. Only measures elements that are
     still hidden. Reveals anything on screen or already scrolled past.
     Runs when idle at boot, again whenever scrolling pauses, and on
     Back/Forward restores. The reveal observer always gets first chance.

     All rects are read first, then all classes written — interleaving
     them made every scroll pause a layout thrash. */
  function sweepHidden() {
    var hidden = document.querySelectorAll(HIDDEN);
    if (!hidden.length) return;
    var vh = window.innerHeight, show = [], i;
    for (i = 0; i < hidden.length; i++) {
      if (hidden[i].getBoundingClientRect().top < vh) show.push(hidden[i]);
    }
    for (i = 0; i < show.length; i++) show[i].classList.add('sd-vis');
  }

  function watchdog() {
    rIC(sweepHidden, { timeout: 1500 });
  }

  var wdTimer = 0;
  function watchdogSoon() {
    clearTimeout(wdTimer);
    wdTimer = setTimeout(function () {
      rIC(sweepHidden, { timeout: 500 });
    }, 700);
  }

  /* ---------- 4. cursor glow spring + scroll parallax ---------- */
  var tx = 0, ty = 0, cx = 0, cy = 0, vx = 0, vy = 0;
  var py = 0, tpy = 0;
  var raf = 0, idle = 0, asleep = false;

  function paintGlow() {
    if (!blobA || !blobB) return;
    // direct transform writes — no custom properties, no repaint
    blobA.style.transform =
      'translate3d(' + cx.toFixed(1) + 'px,' + (cy + py).toFixed(1) + 'px,0)';
    blobB.style.transform =
      'translate3d(' + (-cx * 0.4).toFixed(1) + 'px,' + (-cy * 0.4 + py).toFixed(1) + 'px,0)';
  }

  function loop() {
    var ax = (tx - cx) * 0.055 - vx * 0.22;
    var ay = (ty - cy) * 0.055 - vy * 0.22;
    vx += ax; vy += ay;
    cx += vx; cy += vy;
    py += (tpy - py) * 0.09;

    paintGlow();

    var moving = Math.abs(vx) + Math.abs(vy) + Math.abs(tpy - py) > 0.05;
    idle = moving ? 0 : idle + 1;
    if (idle > 90) { raf = 0; return; }   // sleep when nothing is happening
    raf = requestAnimationFrame(loop);
  }
  function wake() {
    if (tier === 'lite' || asleep) return;
    if (!raf) { idle = 0; raf = requestAnimationFrame(loop); }
  }

  function onMove(e) {
    tx = (e.clientX - window.innerWidth / 2) * 0.16;
    ty = (e.clientY - window.innerHeight / 2) * 0.16;
    wake();
  }

  /* scroll handler does no work of its own — it only sets a target
     and lets the rAF loop do the writing */
  function onScroll() {
    tpy = Math.min(window.scrollY * 0.06, 160);
    wake();
  }

  function stopGlow() {
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    if (blobA) { blobA.style.transform = 'translate3d(0,0,0)'; blobA.style.willChange = 'auto'; }
    if (blobB) { blobB.style.transform = 'translate3d(0,0,0)'; blobB.style.willChange = 'auto'; }
  }

  /* ---------- 4b. live fps probe → tier demotion ---------- */
  function demote() {
    tier = (tier === 'full') ? 'mid' : 'lite';
    root.setAttribute('data-sd-tier', tier);
    if (tier === 'lite') stopGlow();
    document.dispatchEvent(new CustomEvent('sd:tier', { detail: { tier: tier } }));
  }

  function probeFps() {
    if (tier === 'lite' || reduce) return;
    var frames = 0, start = 0;
    function step(now) {
      if (!start) { start = now; frames = 0; requestAnimationFrame(step); return; }
      frames++;
      if (now - start < 1800) { requestAnimationFrame(step); return; }
      var fps = frames / ((now - start) / 1000);
      if (fps < 48) demote();
    }
    requestAnimationFrame(step);
  }

  /* ---------- 5. card tilt + magnetic buttons ---------- */
  function onTiltMove(e) {
    var el = e.currentTarget, r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;
    var px = (e.clientX - r.left) / r.width - 0.5;
    var qy = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty('--sd-ry', (px * 9).toFixed(2) + 'deg');
    el.style.setProperty('--sd-rx', (-qy * 9).toFixed(2) + 'deg');
  }
  function onTiltLeave(e) {
    var el = e.currentTarget;
    el.style.setProperty('--sd-rx', '0deg');
    el.style.setProperty('--sd-ry', '0deg');
  }
  function onMagMove(e) {
    var el = e.currentTarget, r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;
    var mx = (e.clientX - (r.left + r.width / 2)) * 0.28;
    var my = (e.clientY - (r.top + r.height / 2)) * 0.34;
    el.style.setProperty('--sd-mx', Math.max(-14, Math.min(14, mx)).toFixed(2) + 'px');
    el.style.setProperty('--sd-my', Math.max(-10, Math.min(10, my)).toFixed(2) + 'px');
  }
  function onMagLeave(e) {
    var el = e.currentTarget;
    el.style.setProperty('--sd-mx', '0px');
    el.style.setProperty('--sd-my', '0px');
  }

  function bindPointer(ctx) {
    if (reduce || !fine) return;
    each('[data-sd-tilt]', ctx, function (el) {
      if (el.__sdT) return; el.__sdT = 1;
      el.addEventListener('pointermove', onTiltMove, { passive: true });
      el.addEventListener('pointerleave', onTiltLeave, { passive: true });
    });
    each('[data-sd-mag]', ctx, function (el) {
      if (el.__sdM) return; el.__sdM = 1;
      el.addEventListener('pointermove', onMagMove, { passive: true });
      el.addEventListener('pointerleave', onMagLeave, { passive: true });
    });
  }

  /* ---------- 6. page transition + tap zoom ---------- */
  var SPRING = 'linear(0, 0.095, 0.289, 0.501, 0.687, 0.83, 0.928, 0.988, 1.019, 1.032, 1.033, 1.024, 1.011, 1.003, 0.999, 0.999, 1, 1)';
  var zoomEl = null, zoomT = 0;

  /* The photo the shopper is actually looking at inside a card: the
     current swipe-track slide on phones, the hover image on desktop,
     otherwise the main photo. Picks the visible image covering the
     most of the card frame; ties go to the later (top-painted) one. */
  function shotFor(card) {
    var media = card.querySelector('.sd-card__media');
    if (!media) return null;
    var box = media.getBoundingClientRect();
    var imgs = media.querySelectorAll('img'), best = null, bestArea = 0;
    for (var i = 0; i < imgs.length; i++) {
      var r = imgs[i].getBoundingClientRect();
      var w = Math.min(r.right, box.right) - Math.max(r.left, box.left);
      var h = Math.min(r.bottom, box.bottom) - Math.max(r.top, box.top);
      if (w <= 0 || h <= 0) continue;
      var cs = getComputedStyle(imgs[i]);
      if (cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.5) continue;
      if (w * h >= bestArea) { best = imgs[i]; bestArea = w * h; }
    }
    return best;
  }

  /* Sharper copy of a Shopify CDN photo, sized for the full screen.
     Card photos are ~700px wide — soft once they fill a phone. */
  function screenSrc(img, src) {
    if (!/[?&]width=\d+/.test(src)) return '';
    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    var ratio = (img.naturalWidth && img.naturalHeight) ? img.naturalWidth / img.naturalHeight : 0.8;
    // object-fit: cover — the photo is as wide as the screen or as tall as it, whichever is bigger
    var need = Math.max(window.innerWidth, window.innerHeight * ratio) * dpr;
    var w = Math.min(1800, Math.ceil(need / 100) * 100);
    var cur = parseInt((src.match(/[?&]width=(\d+)/) || [])[1], 10) || 0;
    if (w <= cur) return '';
    return src.replace(/([?&]width=)\d+/, '$1' + w);
  }

  function unzoom() {
    clearTimeout(zoomT);
    if (zoomEl) { zoomEl.remove(); zoomEl = null; }
  }

  /* The tapped photo springs from where it is to fill the whole screen,
     instantly. The page keeps loading behind it, then crossfades in. */
  function zoom(img) {
    if (reduce || zoomEl || !img) return false;
    var src = img.currentSrc || img.src;
    var a = img.getBoundingClientRect();
    if (!src || !a.width || !a.height) return false;
    var vw = window.innerWidth, vh = window.innerHeight;

    var wrap = document.createElement('div');
    wrap.className = 'sd-zoom';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML =
      '<div class="sd-zoom__scrim"></div>' +
      '<div class="sd-zoom__shade"></div>' +
      '<div class="sd-zoom__bar"></div>';

    var g = document.createElement('img');
    g.className = 'sd-zoom__shot';
    g.alt = '';
    g.src = src;                       // already in cache — shows instantly
    g.style.left = '0px';
    g.style.top = '0px';
    g.style.width = vw + 'px';
    g.style.height = vh + 'px';
    wrap.insertBefore(g, wrap.children[1]);   // above the glass, below the shade + line
    document.body.appendChild(wrap);
    zoomEl = wrap;

    var from = {
      left: a.left + 'px', top: a.top + 'px',
      width: a.width + 'px', height: a.height + 'px',
      borderRadius: '10px'
    };
    var to = {
      left: '0px', top: '0px',
      width: vw + 'px', height: vh + 'px',
      borderRadius: '0px'
    };
    if (g.animate) {
      try { g.animate([from, to], { duration: 640, easing: SPRING, fill: 'both' }); }
      catch (e) { g.animate([from, to], { duration: 580, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both' }); }
    }

    // sharper copy at low priority so it never competes with the page
    var hi = screenSrc(img, src);
    if (hi) {
      var pre = new Image();
      try { pre.fetchPriority = 'low'; } catch (e) {}
      pre.decoding = 'async';
      pre.onload = function () { if (zoomEl === wrap) g.src = hi; };
      pre.src = hi;
    }

    // glass, edges and loading line fade in on the next frame
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { wrap.classList.add('is-on'); });
    });

    // tap to cancel and stay here
    wrap.addEventListener('click', function () {
      try { window.stop(); } catch (e) {}
      unzoom();
    });

    // never get stuck behind it
    zoomT = setTimeout(unzoom, 15000);
    return true;
  }

  /* shared with sd-quick-add.js and sd-switcher.js:
       SDMorph.can       → true when the zoom will run
       SDMorph.zoom(img) → that photo fills the screen while the page loads
       SDMorph.name(img) → same (older name, kept so those files work) */
  window.SDMorph = { can: !reduce, zoom: zoom, name: zoom };

  function buildFade() {
    /* the black curtain only exists where View Transitions don't */
    var f = null;
    if (!reduce && !canVT) {
      f = document.createElement('div');
      f.className = 'sd-fade';
      f.setAttribute('aria-hidden', 'true');
      document.body.appendChild(f);
    }

    window.addEventListener('pageshow', function () {
      if (f) f.classList.remove('sd-fade--on');
      unzoom();
    });

    if (reduce) return;

    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      if (a.target && a.target !== '_self') return;
      if (a.hasAttribute('download') || a.getAttribute('href').charAt(0) === '#') return;
      if (a.host !== window.location.host) return;
      if (/^(mailto|tel):/i.test(a.getAttribute('href'))) return;
      if (a.closest('[data-sd-still]')) return;

      var card = a.closest('.sd-card');
      if (card && /\/products\//.test(a.pathname)) {
        /* every browser: instant full-screen zoom, then the browser
           navigates natively (no preventDefault — loading starts now) */
        zoom(shotFor(card));
        return;
      }

      /* other links: the browser crossfades where it can */
      if (canVT) return;

      e.preventDefault();
      f.classList.add('sd-fade--on');
      /* start loading the next page now — the fade keeps playing over
         the old page while the new one downloads (was a 300ms wait) */
      window.location.href = a.href;
    });
  }

  /* ---------- 7. scoped rescan, batched ----------
     All new nodes from one idle slice share ONE read pass and ONE write
     pass. Nodes that sit inside another queued node are dropped first,
     since tagging the outer one already covers them. */
  var pending = [], scheduled = 0;

  function topLevelOnly(nodes) {
    var set = new Set(), out = [], i;
    for (i = 0; i < nodes.length; i++) {
      if (nodes[i].isConnected) set.add(nodes[i]);
    }
    set.forEach(function (n) {
      var p = n.parentNode;
      while (p && p !== document) {
        if (set.has(p)) return; // an ancestor is already queued
        p = p.parentNode;
      }
      out.push(n);
    });
    return out;
  }

  function scheduleScan(node) {
    if (node && node.nodeType === 1) pending.push(node);
    if (scheduled) return;
    scheduled = 1;
    rIC(function () {
      scheduled = 0;
      var nodes = topLevelOnly(pending);
      pending = [];
      var limit = Math.min(nodes.length, 40);
      if (!limit) return;

      var plan = null, i;
      for (i = 0; i < limit; i++) plan = tagRead(nodes[i], plan); // all reads
      tagWrite(plan);                                            // all writes
      for (i = 0; i < limit; i++) { observe(nodes[i]); bindPointer(nodes[i]); }
    }, { timeout: 400 });
  }

  /* ---------- 8. boot ---------- */
  function init() {
    if (!document.body) return;

    buildBg();

    // every boot read first, then every boot write → one style recalc
    var liftWant = liftRead();
    var plan = tagRead(document);
    liftWrite(liftWant);
    tagWrite(plan);

    skipOffscreenSections();
    observe(document);
    bindPointer(document);
    buildFade();
    watchdog();

    if (!reduce && tier !== 'lite') {
      if (fine) window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('scroll', onScroll, { passive: true });
      wake();
    }

    // watchdog again whenever scrolling pauses (cheap: hidden elements only)
    window.addEventListener('scroll', watchdogSoon, { passive: true });
    window.addEventListener('resize', watchdogSoon);

    // Back/Forward restore from the page cache: scripts don't rerun
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) { recheck(document); watchdog(); }
    });

    // stop burning frames on a hidden tab
    document.addEventListener('visibilitychange', function () {
      asleep = document.hidden;
      if (asleep) { if (raf) { cancelAnimationFrame(raf); raf = 0; } }
      else { wake(); }
    });

    // re-scan ONLY what Shopify actually inserted
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) scheduleScan(added[j]);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });

    // theme editor
    document.addEventListener('shopify:section:load', function (e) {
      tag(e.target); observe(e.target); bindPointer(e.target);
    });

    // measure real frame rate once the page has settled, then demote if needed
    window.addEventListener('load', function () {
      setTimeout(probeFps, 900);

      /* Safety net: warm anything still skipped — one section per idle
         slice, so no single task lays out and paints a big chunk of the
         page at once. */
      rIC(function () {
        var left = [].slice.call(document.querySelectorAll('.sd-cv'));
        if (!left.length) return;

        (function chunk() {
          var batch = left.splice(0, 1);
          for (var i = 0; i < batch.length; i++) {
            if (warmIO) warmIO.unobserve(batch[i]);
            batch[i].classList.remove('sd-cv');
          }
          requestAnimationFrame(function () {
            for (var j = 0; j < batch.length; j++) recheck(batch[j]);
          });
          if (left.length) rIC(chunk, { timeout: 800 });
        })();
      }, { timeout: 4000 });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();